import DOMPurify from 'isomorphic-dompurify'
import { useTranslation } from 'next-i18next'
import { ConditonalWrapper } from 'shared/components/conditional-wrapper'
import PinIcon from 'shared/icons/pin-icon'
import { formatTimestampToReadableDate } from 'shared/utils/date-time/format-timestamp-to-readable-date'
import { getShortTimeLabel } from 'shared/utils/date-time/get-short-time-label'
import { UserInfoPopover } from 'modules/community/components/user-info-popover/user-info-popover'

interface CommentHeaderProps {
  userName: string
  userId: number
  isCommunity?: boolean
  commentTimestamp: number
  wasEdited: boolean
  isPinned: boolean
  highlightedAuthorHTML?: string
}

export const CommentHeader = ({
  userName,
  userId,
  isCommunity = false,
  commentTimestamp,
  wasEdited,
  isPinned,
  highlightedAuthorHTML,
}: CommentHeaderProps) => {
  const { t } = useTranslation()

  return (
    <div className="mr-8 flex flex-col items-start">
      <div className="flex items-center gap-3">
        <ConditonalWrapper
          when={isCommunity}
          wrapper={children => <UserInfoPopover userId={userId}>{children}</UserInfoPopover>}
        >
          <span
            className="max-w-[150px] truncate text-text-primary sm:max-w-sm lg:max-w-lg"
            title={userName}
            dangerouslySetInnerHTML={{
              __html: highlightedAuthorHTML ? DOMPurify.sanitize(highlightedAuthorHTML) : userName,
            }}
          />
        </ConditonalWrapper>
        {isPinned && (
          <p className="flex items-center gap-1 text-sm text-text-tertiary">
            <PinIcon /> <span>{t('comments.pinned')}</span>
          </p>
        )}
      </div>

      <div
        className="mt-2 text-[15px] leading-5 text-text-tertiary"
        title={formatTimestampToReadableDate(commentTimestamp)}
      >
        {getShortTimeLabel(new Date(commentTimestamp), t)}
        {wasEdited && ` • (${t('comments.state.edited')})`}
      </div>
    </div>
  )
}
